import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import Cookies from 'universal-cookie';

import {
  getConfig, subscribe, APP_I18N_INITIALIZED,
} from '@edx/frontend-platform';
import { AppContext } from '@edx/frontend-platform/react';
import { Icon } from '@openedx/paragon';
import {
  Close, ExpandMore, MenuIcon, Nightlight, WbSunny,
} from '@openedx/paragon/icons';
import { useIntl, mergeMessages } from '@edx/frontend-platform/i18n';

function addPlugins(config, slot_name, plugins) {
  if (slot_name in config.pluginSlots === false) {
    config.pluginSlots[slot_name] = {
      keepDefault: true,
      plugins: []
    };
  }

  config.pluginSlots[slot_name].plugins.push(...plugins);
}
let themeVariant = 'selected-paragon-theme-variant';

const AddDarkTheme = () => {
  const isThemeToggleEnabled = getConfig().INDIGO_ENABLE_DARK_TOGGLE;

  const addDarkThemeToIframes = () => {
    const iframes = document.getElementsByTagName('iframe');
    const iframesLength = iframes.length;
    if (iframesLength > 0) {
      Array.from({ length: iframesLength }).forEach((_, index) => {
        const style = document.createElement('style');
        style.textContent = `
          body {
            background-color: #0D0D0E;
            color: #ccc;
          }
          a { color: #ccc; }
          a:hover { color: #d3d3d3; }
        `;
        if (iframes[index].contentDocument) {
          iframes[index].contentDocument.head.appendChild(style);
        }
      });
    }
  };

  useEffect(() => {
    const theme = window.localStorage.getItem(themeVariant);

    // - When page loads, Footer loads before MFE content. Since there is no iframe on page,
    // it does not append any class. MutationObserver observes changes in DOM and hence appends dark
    // attributes when iframe is added. After 15 sec, this observer is destroyed to conserve resources. 
    // - It has been added outside dark-theme condition so that it can be removed on Component Unmount.
    // - Observer can be passed to `addDarkThemeToIframes` function and disconnected after observing Iframe.
    // This approach has a limitation: the observer first detects the iframe and then detects the docSrc. 
    // We need to wait for docSrc to fully load before appending the style tag.
    const observer = new MutationObserver(() => {
      addDarkThemeToIframes();
    });

    if (isThemeToggleEnabled && theme === 'dark') {
      document.documentElement.setAttribute('data-paragon-theme-variant', 'dark');

      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => observer?.disconnect(), 15000); // clear after 15 sec to avoid resource usage
    }

    return () => observer?.disconnect();
  }, []);

  return (<div />);
};

async function setConfig () {
  let config = {
    pluginSlots: {}
  };

  try {
    /* We can't assume FPF exists, as it's not declared as a dependency in all
     * MFEs, so we import it dynamically. In addition, for dynamic imports to
     * work with Webpack all of the code that actually uses the imported module
     * needs to be inside the `try{}` block.
     */
    const { DIRECT_PLUGIN, PLUGIN_OPERATIONS } = await import('@openedx/frontend-plugin-framework');const FOOTER_BG = "#76a5a8";

const FOOTER_COLUMNS = [
  {
    titleMessageKey: "footer.column.links",
    links: [
      { messageKey: "footer.links.news", href: "/pages/news/" },
      { messageKey: "footer.links.privacyStatement", href: "/privacy" },
      { messageKey: "footer.links.termsOfUse", href: "/tos" },
      { messageKey: "footer.links.faq", href: "/faq" },
      { messageKey: "footer.links.youtube", href: "https://www.youtube.com/@finishingx_EN" },
    ],
  },
  {
    titleMessageKey: "footer.column.contactUs",
    links: [
      { messageKey: "footer.contact.email", href: "mailto:info@finishingx.de" },
      { messageKey: "footer.contact.schedulePhoneCall", href: "/pages/schedule-meeting/" },
      { messageKey: "footer.contact.aboutUs", href: "/about" },
    ],
  },
  {
    titleMessageKey: "footer.column.shop",
    links: [
      { messageKey: "footer.shop.tryFree", href: "/register" },
      { messageKey: "footer.shop.pricing", href: "/pages/pricing-table/" },
      { messageKey: "footer.shop.subscribe", href: "/pages/subscribe/" },
      { messageKey: "footer.shop.cancelMembership", href: "/pages/cancel_membership_form/" },
      { messageKey: "footer.shop.revocation", href: "/pages/withdrawal_form/" },
      { messageKey: "footer.shop.webinars", href: "/pages/webinar/" },
      { messageKey: "footer.shop.consulting", href: "/pages/Arbeitsschutzberatung/" },
    ],
  },
];

const resolveFooterHref = (href, lmsBase) => {
  if (!href) {
    return "#";
  }
  if (/^(https?:|mailto:|tel:)/i.test(href)) {
    return href;
  }
  const path = href.startsWith("/") ? href : `/${href}`;
  const base = (lmsBase || "").replace(/\/$/, "");
  return `${base}${path}`;
};

const IndigoFooter = () => {
  const intl = useIntl();
  const config = getConfig();
  const lmsBase = config.LMS_BASE_URL || "";
  const year = new Date().getFullYear();

  const messages = {
    "footer.column.links": {
      id: "footer.column.links",
      defaultMessage: "Links",
      description: "Footer column heading for general links",
    },
    "footer.column.contactUs": {
      id: "footer.column.contactUs",
      defaultMessage: "Contact us",
      description: "Footer column heading for contact information",
    },
    "footer.column.shop": {
      id: "footer.column.shop",
      defaultMessage: "Shop",
      description: "Footer column heading for shop-related links",
    },
    "footer.links.news": {
      id: "footer.links.news",
      defaultMessage: "News",
      description: "Footer link: news",
    },
    "footer.links.privacyStatement": {
      id: "footer.links.privacyStatement",
      defaultMessage: "Privacy statement",
      description: "Footer link: privacy statement",
    },
    "footer.links.termsOfUse": {
      id: "footer.links.termsOfUse",
      defaultMessage: "Terms of use",
      description: "Footer link: terms of use",
    },
    "footer.links.faq": {
      id: "footer.links.faq",
      defaultMessage: "FAQ",
      description: "Footer link: FAQ",
    },
    "footer.links.youtube": {
      id: "footer.links.youtube",
      defaultMessage: "YouTube",
      description: "Footer link: YouTube channel",
    },
    "footer.contact.email": {
      id: "footer.contact.email",
      defaultMessage: "info@finishingx.de",
      description: "Footer contact email (link label and mailto)",
    },
    "footer.contact.schedulePhoneCall": {
      id: "footer.contact.schedulePhoneCall",
      defaultMessage: "Schedule phone call",
      description: "Footer link: schedule a phone call",
    },
    "footer.contact.aboutUs": {
      id: "footer.contact.aboutUs",
      defaultMessage: "About us",
      description: "Footer link: about us",
    },
    "footer.shop.tryFree": {
      id: "footer.shop.tryFree",
      defaultMessage: "Try it for free",
      description: "Footer shop link: free trial",
    },
    "footer.shop.pricing": {
      id: "footer.shop.pricing",
      defaultMessage: "Pricing",
      description: "Footer shop link: pricing",
    },
    "footer.shop.subscribe": {
      id: "footer.shop.subscribe",
      defaultMessage: "Subscribe",
      description: "Footer shop link: subscribe",
    },
    "footer.shop.cancelMembership": {
      id: "footer.shop.cancelMembership",
      defaultMessage: "Cancel membership",
      description: "Footer shop link: cancel membership",
    },
    "footer.shop.revocation": {
      id: "footer.shop.revocation",
      defaultMessage: "Revocation",
      description: "Footer shop link: revocation / withdrawal policy",
    },
    "footer.shop.webinars": {
      id: "footer.shop.webinars",
      defaultMessage: "Webinars",
      description: "Footer shop link: webinars",
    },
    "footer.shop.consulting": {
      id: "footer.shop.consulting",
      defaultMessage: "Consulting",
      description: "Footer shop link: consulting",
    },
    "footer.cert.zfu.alt": {
      id: "footer.cert.zfu.alt",
      defaultMessage:
        "ZFU seal — Staatliche Zentralstelle für Fernunterricht",
      description: "Alt text for ZFU certification logo in footer",
    },
    "footer.cert.iso.alt": {
      id: "footer.cert.iso.alt",
      defaultMessage: "ISO 9001:2015 certification",
      description: "Alt text for ISO 9001 logo in footer",
    },
    "footer.copyright.finishingx": {
      id: "footer.copyright.finishingx",
      defaultMessage: "© {year} FinishingX. All rights reserved.",
      description: "Footer copyright line for FinishingX",
    },
  };

  const footerCss = `
    .indigo-finishingx-footer {
      --indigo-footer-bg: ${FOOTER_BG};
      --indigo-footer-fg: #ffffff;
      background-color: var(--indigo-footer-bg);
      color: var(--indigo-footer-fg);
      font-family: inherit;
      padding: 2.75rem 1.5rem 2.25rem;
      box-sizing: border-box;
    }
    .indigo-finishingx-footer *,
    .indigo-finishingx-footer *::before,
    .indigo-finishingx-footer *::after {
      box-sizing: border-box;
    }
    .indigo-finishingx-footer a {
      color: var(--indigo-footer-fg);
      text-decoration: none;
    }
    .indigo-finishingx-footer a:hover,
    .indigo-finishingx-footer a:focus {
      text-decoration: underline;
    }
    .indigo-finishingx-footer__inner {
      max-width: 1200px;
      margin: 0 auto;
    }
    .indigo-finishingx-footer__columns {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 2.5rem 3rem;
      align-items: start;
      justify-items: start;
      margin-bottom: 2.75rem;
    }
    @media (max-width: 768px) {
      .indigo-finishingx-footer__columns {
        grid-template-columns: 1fr;
        gap: 2rem;
      }
    }
    .indigo-finishingx-footer__column {
      min-width: 0;
      width: 100%;
      max-width: 22rem;
    }
    .indigo-finishingx-footer__column-title {
      margin: 0 0 1rem;
      padding-top: 0.65rem;
      border-top: 1px solid rgba(255, 255, 255, 0.9);
      width: fit-content;
      max-width: 100%;
      font-size: 18px;
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      line-height: 1.3;
      color: #ffffff;
    }
    .indigo-finishingx-footer__list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.45rem;
      font-size: 0.9375rem;
      font-weight: 400;
      line-height: 1.45;
    }
    .indigo-finishingx-footer__list li a:hover,
    .indigo-finishingx-footer__list li a:focus {
      color: #1e5376;
      text-decoration: none;
    }
    .indigo-finishingx-footer__bottom {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 1.25rem;
      padding-top: 0.25rem;
    }
    .indigo-finishingx-footer__certs {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 1.5rem;
    }
    .indigo-finishingx-footer__cert-img {
      display: block;
      height: 3.25rem;
      width: auto;
      max-width: 140px;
      object-fit: contain;
    }
    .indigo-finishingx-footer__copyright {
      margin: 0;
      font-size: 0.875rem;
      opacity: 0.95;
    }
  `;

  return (
    <>
      <style>{footerCss}</style>
      <div className="wrapper wrapper-footer indigo-finishingx-footer">
        <footer id="footer" className="tutor-container indigo-finishingx-footer__inner">
          <div className="indigo-finishingx-footer__columns">
            {FOOTER_COLUMNS.map((column) => (
              <div
                key={column.titleMessageKey}
                className="indigo-finishingx-footer__column"
              >
                <h2 className="indigo-finishingx-footer__column-title">
                  {intl.formatMessage(messages[column.titleMessageKey])}
                </h2>
                <ul className="indigo-finishingx-footer__list">
                  {column.links.map((link) => (
                    <li key={link.messageKey}>
                      <a
                        href={resolveFooterHref(link.href, lmsBase)}
                        rel={
                          /^https?:/i.test(link.href)
                            ? "noopener noreferrer"
                            : undefined
                        }
                      >
                        {intl.formatMessage(messages[link.messageKey])}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="indigo-finishingx-footer__bottom">
            <div className="indigo-finishingx-footer__certs">
              <img
                className="indigo-finishingx-footer__cert-img"
                src={`${lmsBase}/static/indigo/images/footer-cert-zfu.png`}
                alt={intl.formatMessage(messages["footer.cert.zfu.alt"])}
                loading="lazy"
              />
              <img
                className="indigo-finishingx-footer__cert-img"
                src={`${lmsBase}/static/indigo/images/footer-cert-iso9001.png`}
                alt={intl.formatMessage(messages["footer.cert.iso.alt"])}
                loading="lazy"
              />
            </div>
            <p className="indigo-finishingx-footer__copyright">
              {intl.formatMessage(messages["footer.copyright.finishingx"], {
                year,
              })}
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};
const MobileViewHeader = () => {
  const config = getConfig();
  const intl = useIntl();
  const messages = {
    "mobile.view.header.logo.altText": {
      id: "mobile.view.header.logo.altText",
      defaultMessage: "My Open edX",
      description: "alt text for the mobile view header logo",
    },
  };

  const BASE_URL = config.LMS_BASE_URL;

  return (
    <>
      <style>
        {`
          #root header .logo-image.logo-white {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root header .logo-image {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root header .logo-white {
            display: block;
          }
        `}
      </style>
      <a href={`${BASE_URL}/dashboard`} title="Open edX" className="logo">
        <img className="logo-image" src={`${BASE_URL}/static/indigo/images/logo.png`} alt={intl.formatMessage(messages["mobile.view.header.logo.altText"])} />
        <img className="logo-image logo-white" src={`${BASE_URL}/static/indigo/images/logo-white.png`} alt={intl.formatMessage(messages["mobile.view.header.logo.altText"])} />
      </a>
    </>
  );
};
const ThemedLogo = () => {
  const BASE_URL = getConfig().LMS_BASE_URL;

  return (
    <>
      <style>
        {`
          #root header .logo-image.logo-white {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root header .logo-image {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root header .logo-white {
            display: block;
          }
        `}
      </style>
      <a href={`${BASE_URL}/dashboard`} title="Open edX" className="logo">
        <img className="logo-image" src={`${BASE_URL}/static/indigo/images/logo.png`} alt="Open edX" />
        <img className="logo-image logo-white" src={`${BASE_URL}/static/indigo/images/logo-white.png`} alt="Open edX" />
      </a>
    </>
  );
};
const ToggleThemeButton = () => {
  const intl = useIntl();
  const [isDarkThemeEnabled, setIsDarkThemeEnabled] = useState(false);

  const themeCookie = 'selected-paragon-theme-variant';
  const themeCookieExpiry = 90; // days
  const isThemeToggleEnabled = getConfig().INDIGO_ENABLE_DARK_TOGGLE;

  const getCookie = (name) => {
    return document.cookie
      .split("; ")
      .find((row) => row.startsWith(name + "="))
      ?.split("=")[1];
  };

  const setCookie = (name, value, { domain, path, expires }) => {
    document.cookie = `${name}=${value}; domain=${domain}; path=${path}; expires=${expires.toUTCString()}; SameSite=Lax`;
  };

  const serverURL = new URL(getConfig().LMS_BASE_URL);

  const getCookieExpiry = () => {
    const today = new Date();
    return new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + themeCookieExpiry
    );
  };

  const getCookieOptions = (serverURL) => ({
    domain: serverURL.hostname,
    path: '/',
    expires: getCookieExpiry(),
  });

  const onToggleTheme = () => {
    let theme = '';

    if (getCookie(themeCookie) === 'dark') {
      document.documentElement.setAttribute('data-paragon-theme-variant', 'light');
      setIsDarkThemeEnabled(false);
      theme = 'light';
    } else {
      document.documentElement.setAttribute('data-paragon-theme-variant', 'dark');
      setIsDarkThemeEnabled(true);
      theme = 'dark';
    }

    window.localStorage.setItem(themeCookie, theme);
    setTimeout(() => {
      setCookie(themeCookie, theme, getCookieOptions(serverURL));
      window.location.reload();
    }, 1);
  };

  useEffect(() => {
    if (!getCookie(themeCookie) || getCookie(themeCookie) === 'undefined') {
      return;
    }
    if (getCookie(themeCookie) !== window.localStorage.getItem(themeCookie)) {
      window.localStorage.setItem(themeCookie, getCookie(themeCookie));
      window.location.reload();
    }
  }, []);

  const handleKeyUp = (event) => {
    if (event.key === "Enter") {
      onToggleTheme();
    }
  };

  if (!isThemeToggleEnabled) {
    return <div />;
  }

  const messages = {
    "header.user.theme": {
      id: "header.user.theme",
      defaultMessage: "Toggle Theme",
      description: "Toggle between light and dark theme",
    },
  };

  return (
    <div className="theme-toggle-button mr-3">
      <div className="light-theme-icon">
        <Icon src={WbSunny} />
      </div>
      <div className="toggle-switch">
        <label htmlFor="theme-toggle-checkbox" className="switch">
          <input
            id="theme-toggle-checkbox"
            defaultChecked={getCookie(themeCookie) === "dark"}
            onChange={onToggleTheme}
            onKeyUp={handleKeyUp}
            type="checkbox"
            title={intl.formatMessage(messages["header.user.theme"])}
          />
          <span className="slider round" />
          <span id="theme-label" className="sr-only">{`Switch to ${isDarkThemeEnabled ? "Light" : "Dark"
            } Mode`}</span>
        </label>
      </div>
      <div className="dark-theme-icon">
        <Icon src={Nightlight} />
      </div>
    </div>
  );
};
/**
 * Translations for the strings CustomHeader.jsx / LanguageMenu.jsx render.
 *
 * Why this can't just go through openedx-translations like everything else:
 * this file — like the rest of tutorindigo/components — ships from the
 * *Tutor plugin*, not from any MFE's own `src/` tree. `formatjs extract`
 * (`npm run i18n_extract`) only ever scans an MFE's own source, so these
 * message ids never get extracted into `src/i18n/messages/*.json`, and Atlas
 * has nothing to pull for them from openedx-translations no matter how
 * correctly translations are configured there for a given MFE — that pipeline
 * is scoped to each MFE's own repo. The ids/English defaultMessage text live
 * inline in CustomHeader.jsx/LanguageMenu.jsx (`intl.formatMessage(messages[key])`);
 * this file supplies everything else that pipeline can't reach, and merges it
 * into frontend-platform's live message registry via the same public
 * mergeMessages() API an installed plugin bundle would use for its own
 * strings, so React still renders these through the normal formatMessage()
 * path — no separate lookup mechanism.
 *
 * English needs no entry here — every message's own `defaultMessage` in
 * CustomHeader.jsx/LanguageMenu.jsx already covers it, and duplicating that
 * text here would just be a second copy that could quietly drift out of sync.
 *
 * Only merge after i18n has actually been configured. `@edx/frontend-platform`
 * calls jsFileConfig() — which runs this whole env.config.jsx module — before
 * configureI18n() (see initialize.js's init sequence), so at the time this
 * file's top-level code runs, frontend-platform/i18n's internal `messages` is
 * still `null`; merging into it here would merge into `null` instead of the
 * real catalog. APP_I18N_INITIALIZED fires right after configureI18n()
 * completes and well before the app actually mounts, so subscribing to it
 * guarantees the merge lands before first paint without racing configure().
 *
 * Add another supported language by adding another top-level key here (its
 * locale code — see LanguageMenu.jsx / INDIGO_SUPPORTED_LANGUAGES in
 * plugin.py) with the same id set.
 */
const HEADER_TRANSLATIONS = {
  de: {
    'header.logo.aria': '{siteName} Startseite',
    'header.nav.aria': 'Hauptnavigation',
    'header.nav.dashboard': 'Meine Kurse',
    'header.nav.about': 'Über uns',
    'header.nav.contact': 'Kontakt',
    'header.nav.faq': 'FAQ',
    'header.actions.signIn': 'Anmelden',
    'header.actions.register': 'Registrieren',
    'header.mobile.menu': 'Menü',
    'header.user.menu': 'Benutzermenü',
    'header.user.profile': 'Profil',
    'header.user.account': 'Konto',
    'header.user.studio': 'Studio',
    'header.user.logout': 'Abmelden',
    'header.language.label': 'Sprache',
  },
};

subscribe(APP_I18N_INITIALIZED, () => {
  mergeMessages(HEADER_TRANSLATIONS);
});
/**
 * Header language switcher (rendered inside CustomHeader.jsx). Sets the same
 * LANGUAGE_PREFERENCE_COOKIE_NAME cookie ('openedx-language-preference' by
 * default) that both frontend-platform's i18n (getLocale(), see
 * @edx/frontend-platform/i18n/lib.js) and edx-platform's own
 * LocaleMiddleware/DarkLangMiddleware read — the same mechanism the stock
 * LMS footer language selector uses (openedx.core.djangoapps.lang_pref),
 * just surfaced in the MFE header too. Reload picks up the new locale on
 * both the current MFE and, since the cookie is shared, any Django-rendered
 * page (marketing site, Studio) the user navigates to afterwards.
 *
 * Options come from INDIGO_SUPPORTED_LANGUAGES (MFE_CONFIG, set in
 * plugin.py) — override via:
 *   tutor config save --set 'INDIGO_SUPPORTED_LANGUAGES=[{"value": "en", "label": "English"}]'
 */

/** Module scope, not recreated every render — see HeaderTranslations.jsx for
 * why this id's German string lives there instead of a locale JSON file. */
const LANGUAGE_MENU_MESSAGES = {
  'header.language.label': {
    id: 'header.language.label',
    defaultMessage: 'Language',
    description: 'Header language selector label',
  },
};

const LanguageMenu = () => {
  const intl = useIntl();
  const config = getConfig();
  const languages = config.INDIGO_SUPPORTED_LANGUAGES || [];
  const isEnabled = config.INDIGO_ENABLE_LANGUAGE_MENU !== false;

  // Nothing to switch between with 0 or 1 language configured.
  if (!isEnabled || languages.length < 2) {
    return null;
  }

  const cookieName = config.LANGUAGE_PREFERENCE_COOKIE_NAME || 'openedx-language-preference';
  const serverURL = new URL(config.LMS_BASE_URL);
  const currentLocale = (intl.locale || 'en').toLowerCase();
  const selectedValue = languages.find((lang) => lang.value.toLowerCase() === currentLocale)?.value
    || languages.find((lang) => currentLocale.startsWith(lang.value.toLowerCase()))?.value
    || languages[0].value;

  const onChange = (event) => {
    const cookies = new Cookies();
    cookies.set(cookieName, event.target.value, {
      path: '/',
      domain: serverURL.hostname,
      expires: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000),
      sameSite: 'lax',
    });
    window.location.reload();
  };

  return (
    <div className="indigo-language-menu">
      <label htmlFor="indigo-header-language-select" className="sr-only">
        {intl.formatMessage(LANGUAGE_MENU_MESSAGES['header.language.label'])}
      </label>
      <select
        id="indigo-header-language-select"
        className="indigo-language-menu__select"
        value={selectedValue}
        onChange={onChange}
        aria-label={intl.formatMessage(LANGUAGE_MENU_MESSAGES['header.language.label'])}
      >
        {languages.map(({ value, label }) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>
    </div>
  );
};
/**
 * FinishingX marketing header — replaces the native <Header /> / <LearningHeader />
 * on every Indigo-styled MFE (see HEADER_REPLACEMENT_SLOTS in plugin.py).
 *
 * Ported from the TitanEd/tels tutor-indigo fork's CustomHeader, adapted because:
 *  - FinishingX has no "public" marketing MFE — marketing pages (home/courses/
 *    about/contact) are Mako templates served by ulmo-theme on the LMS domain
 *    itself, so links resolve against LMS_BASE_URL instead of a "/public" MFE
 *    mount (compare IndigoFooter.jsx's resolveFooterHref, same idea).
 *  - No Control Hub app in this deployment — dropped from the user menu.
 *  - Self-contained <style> (no dependency on a brand package shipping
 *    .custom-header/.tels-* classes), matching this repo's existing
 *    IndigoFooter.jsx / ThemedLogo.jsx / MobileViewHeader.jsx convention.
 *  - Primary nav is intentionally just About Us / Contact Us / FAQ (see
 *    NAV_KEYS) — Home is the logo, Dashboard lives in the user menu.
 *  - <LanguageMenu /> (LanguageMenu.jsx) renders a language <select> here
 *    when 2+ languages are configured (INDIGO_SUPPORTED_LANGUAGES).
 *
 * Slot widget ids used (see plugin.py _custom_header_plugins()):
 *   custom_header_desktop / custom_header_mobile — native <Header /> MFEs
 *   custom_header_learning                       — <LearningHeader /> MFEs
 */

const CUSTOM_HEADER_CSS = `
  .indigo-custom-header {
    --indigo-header-bg: #ffffff;
    --indigo-header-fg: #1b1b1b;
    --indigo-header-accent: #6EACAF;
    --indigo-header-border: #e5e5e5;
    background-color: var(--indigo-header-bg);
    color: var(--indigo-header-fg);
    border-bottom: 1px solid var(--indigo-header-border);
    box-sizing: border-box;
  }
  [data-paragon-theme-variant="dark"] .indigo-custom-header {
    --indigo-header-bg: #0D0D0E;
    --indigo-header-fg: #f2f2f2;
    --indigo-header-border: rgba(255, 255, 255, 0.15);
  }
  .indigo-custom-header *,
  .indigo-custom-header *::before,
  .indigo-custom-header *::after {
    box-sizing: border-box;
  }
  .indigo-custom-header a {
    color: inherit;
    text-decoration: none;
  }
  .indigo-custom-header__row {
    max-width: 1600px;
    margin: 0 auto;
    padding: 0.75rem 1.5rem;
    display: flex;
    align-items: center;
    gap: 1.5rem;
  }
  .indigo-custom-header__logo img {
    display: block;
    height: 2.25rem;
    width: auto;
  }
  .indigo-custom-header__logo .logo-white { display: none; }
  [data-paragon-theme-variant="dark"] .indigo-custom-header__logo .logo-default { display: none; }
  [data-paragon-theme-variant="dark"] .indigo-custom-header__logo .logo-white { display: block; }
  .indigo-custom-header__nav {
    flex: 1 1 auto;
    display: flex;
    gap: 1.5rem;
  }
  .indigo-custom-header__nav-link {
    font-size: 0.9375rem;
    font-weight: 500;
    padding: 0.25rem 0;
    border-bottom: 2px solid transparent;
    white-space: nowrap;
    display: none;
  }
  .indigo-custom-header__nav-link:hover,
  .indigo-custom-header__nav-link:focus,
  .indigo-custom-header__nav-link.active {
    border-bottom-color: var(--indigo-header-accent);
  }
  .indigo-custom-header__actions {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  .indigo-custom-header__btn {
    display: inline-block;
    padding: 0.4rem 1rem;
    border-radius: 0.25rem;
    font-size: 0.875rem;
    font-weight: 600;
    white-space: nowrap;
  }
  .indigo-custom-header__btn--ghost {
    border: 1px solid var(--indigo-header-accent);
    color: var(--indigo-header-accent);
  }
  .indigo-custom-header__btn--primary {
    background-color: var(--indigo-header-accent);
    color: #ffffff;
  }
  .indigo-language-menu__select {
    background-color: transparent;
    color: inherit;
    border: 1px solid var(--indigo-header-border);
    border-radius: 0.25rem;
    padding: 0.3rem 0.5rem;
    font-size: 0.8125rem;
    font-weight: 500;
    cursor: pointer;
  }
  [data-paragon-theme-variant="dark"] .indigo-language-menu__select option {
    color: #1b1b1b;
  }
  .indigo-custom-header__mobile-panel .indigo-language-menu {
    padding: 0.5rem 0;
  }
  .indigo-custom-header__user {
    position: relative;
  }
  .indigo-custom-header__user-toggle {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    background: none;
    border: none;
    color: inherit;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
    padding: 0.25rem 0;
  }
  .indigo-custom-header__user-menu {
    position: absolute;
    right: 0;
    top: calc(100% + 0.5rem);
    background-color: var(--indigo-header-bg);
    border: 1px solid var(--indigo-header-border);
    border-radius: 0.25rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    list-style: none;
    margin: 0;
    padding: 0.5rem 0;
    min-width: 12rem;
    z-index: 1030;
  }
  .indigo-custom-header__user-menu li a {
    display: block;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    white-space: nowrap;
  }
  .indigo-custom-header__user-menu li a:hover,
  .indigo-custom-header__user-menu li a:focus {
    background-color: rgba(110, 172, 175, 0.12);
  }
  .indigo-custom-header__mobile-toggle {
    display: none;
    background: none;
    border: none;
    color: inherit;
    cursor: pointer;
    padding: 0.25rem;
  }
  .indigo-custom-header__mobile-panel {
    display: none;
    flex-direction: column;
    gap: 0.25rem;
    padding: 0.5rem 1.5rem 1rem;
    border-top: 1px solid var(--indigo-header-border);
  }
  .indigo-custom-header__mobile-panel a {
    padding: 0.5rem 0;
    font-size: 0.9375rem;
    font-weight: 500;
  }
  @media (max-width: 768px) {
    .indigo-custom-header__nav,
    .indigo-custom-header__actions {
      display: none;
    }
    .indigo-custom-header__mobile-toggle {
      display: inline-flex;
      margin-left: auto;
    }
    .indigo-custom-header__mobile-panel.open {
      display: flex;
    }
  }
`;

/** Marketing routes served by ulmo-theme Mako templates on the LMS domain
 * (static_template_view — same routes IndigoFooter.jsx already links to). */
const NAV_ROUTE_BY_KEY = {
  about: '/about',
  contact: '/contact',
  faq: '/faq',
};

/** Primary nav — About Us / Contact Us / FAQ only, for both guest and
 * authenticated users. Home is still reachable via the logo, and Dashboard
 * via the authenticated user menu below. */
const NAV_KEYS = ['about', 'contact', 'faq'];

/** Resolve a marketing/app path against LMS_BASE_URL — same rule as
 * IndigoFooter.jsx's resolveFooterHref, kept local so this file stays
 * self-contained like the rest of tutorindigo/components. */
const resolveHref = (path, lmsBase) => {
  if (/^https?:/i.test(path)) {
    return path;
  }
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${(lmsBase || '').replace(/\/$/, '')}${normalized}`;
};

/** Message descriptors — module scope (not recreated on every render, and
 * the conventional react-intl shape even though nothing here actually gets
 * i18n_extract'd from this file — see HeaderTranslations.jsx for why, and for
 * the German strings these ids resolve to). */
const HEADER_MESSAGES = {
  'header.logo.aria': {
    id: 'header.logo.aria',
    defaultMessage: '{siteName} Home',
    description: 'Aria label for the header logo home link',
  },
  'header.nav.aria': {
    id: 'header.nav.aria',
    defaultMessage: 'Primary',
    description: 'Aria label for primary navigation',
  },
  'header.nav.dashboard': {
    id: 'header.nav.dashboard',
    defaultMessage: 'Dashboard',
    description: 'User menu Dashboard link',
  },
  'header.nav.about': {
    id: 'header.nav.about',
    defaultMessage: 'About Us',
    description: 'Header About Us link',
  },
  'header.nav.contact': {
    id: 'header.nav.contact',
    defaultMessage: 'Contact Us',
    description: 'Header Contact Us link',
  },
  'header.nav.faq': {
    id: 'header.nav.faq',
    defaultMessage: 'FAQ',
    description: 'Header FAQ link',
  },
  'header.actions.signIn': {
    id: 'header.actions.signIn',
    defaultMessage: 'Sign In',
    description: 'Header Sign In button',
  },
  'header.actions.register': {
    id: 'header.actions.register',
    defaultMessage: 'Register',
    description: 'Header Register button',
  },
  'header.mobile.menu': {
    id: 'header.mobile.menu',
    defaultMessage: 'Menu',
    description: 'Mobile menu toggle aria label',
  },
  'header.user.menu': {
    id: 'header.user.menu',
    defaultMessage: 'User menu',
    description: 'Authenticated user menu toggle aria label',
  },
  'header.user.profile': {
    id: 'header.user.profile',
    defaultMessage: 'Profile',
    description: 'User dropdown Profile link',
  },
  'header.user.account': {
    id: 'header.user.account',
    defaultMessage: 'Account',
    description: 'User dropdown Account settings link',
  },
  'header.user.studio': {
    id: 'header.user.studio',
    defaultMessage: 'Studio',
    description: 'Studio link label in the user menu (course staff only)',
  },
  'header.user.logout': {
    id: 'header.user.logout',
    defaultMessage: 'Sign Out',
    description: 'User dropdown Sign Out link',
  },
};

const CustomHeader = () => {
  const intl = useIntl();
  const config = getConfig();
  const { authenticatedUser } = useContext(AppContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const lmsBase = config.LMS_BASE_URL || '';
  const siteName = config.SITE_NAME || 'FinishingX';
  const logoUrl = `${lmsBase}/static/indigo/images/logo.png`;
  const logoWhiteUrl = `${lmsBase}/static/indigo/images/logo-white.png`;
  const dashboardUrl = `${lmsBase}/dashboard`;
  const loginUrl = config.LOGIN_URL;
  const registerUrl = config.REGISTER_URL || `${lmsBase}/register`;
  const logoutUrl = config.LOGOUT_URL;
  const profileUrl = authenticatedUser && config.ACCOUNT_PROFILE_URL
    ? `${config.ACCOUNT_PROFILE_URL}/u/${authenticatedUser.username}`
    : null;
  const accountUrl = config.ACCOUNT_SETTINGS_URL;
  const studioUrl = config.STUDIO_BASE_URL;
  const isCourseStaff = authenticatedUser?.administrator === true;

  const userMenuLinks = authenticatedUser ? [
    profileUrl && {
      key: 'profile',
      href: profileUrl,
      messageKey: 'header.user.profile',
    },
    accountUrl && {
      key: 'account',
      href: accountUrl,
      messageKey: 'header.user.account',
    },
    {
      key: 'dashboard',
      href: dashboardUrl,
      messageKey: 'header.nav.dashboard',
    },
    isCourseStaff && studioUrl && {
      key: 'studio',
      href: studioUrl,
      messageKey: 'header.user.studio',
    },
    {
      key: 'logout',
      href: logoutUrl,
      messageKey: 'header.user.logout',
    },
  ].filter(Boolean) : [];

  useEffect(() => {
    const onDocClick = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const hrefForKey = (navKey) => resolveHref(NAV_ROUTE_BY_KEY[navKey], lmsBase);

  const renderNavLink = (navKey, { onClick, keyPrefix = '' } = {}) => (
    <a
      key={`${keyPrefix}${navKey}`}
      href={hrefForKey(navKey)}
      className="indigo-custom-header__nav-link"
      onClick={onClick}
    >
      {intl.formatMessage(HEADER_MESSAGES[`header.nav.${navKey}`])}
    </a>
  );

  const closeMobile = () => setMobileOpen(false);
  const closeUserMenu = () => setUserMenuOpen(false);

  return (
    <>
      <style>{CUSTOM_HEADER_CSS}</style>
      <header className="indigo-custom-header">
        <div className="indigo-custom-header__row">
          <a
            href={resolveHref('/', lmsBase)}
            className="indigo-custom-header__logo"
            aria-label={intl.formatMessage(HEADER_MESSAGES['header.logo.aria'], { siteName })}
          >
            <img className="logo-default" src={logoUrl} alt={siteName} />
            <img className="logo-white" src={logoWhiteUrl} alt={siteName} />
          </a>

          <nav
            className="indigo-custom-header__nav"
            aria-label={intl.formatMessage(HEADER_MESSAGES['header.nav.aria'])}
          >
            {NAV_KEYS.map((navKey) => renderNavLink(navKey))}
          </nav>

          <div className="indigo-custom-header__actions">
            <LanguageMenu />
            {authenticatedUser ? (
              <div className="indigo-custom-header__user" ref={userMenuRef}>
                <button
                  type="button"
                  className="indigo-custom-header__user-toggle"
                  aria-label={intl.formatMessage(HEADER_MESSAGES['header.user.menu'])}
                  aria-expanded={userMenuOpen}
                  onClick={() => setUserMenuOpen((open) => !open)}
                >
                  {authenticatedUser.name || authenticatedUser.username}
                  <Icon src={ExpandMore} />
                </button>
                {userMenuOpen && (
                  <ul className="indigo-custom-header__user-menu">
                    {userMenuLinks.map((item) => (
                      <li key={item.key}>
                        <a href={item.href} onClick={closeUserMenu}>
                          {intl.formatMessage(HEADER_MESSAGES[item.messageKey])}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <>
                <a href={loginUrl} className="indigo-custom-header__btn indigo-custom-header__btn--ghost">
                  {intl.formatMessage(HEADER_MESSAGES['header.actions.signIn'])}
                </a>
                <a href={registerUrl} className="indigo-custom-header__btn indigo-custom-header__btn--primary">
                  {intl.formatMessage(HEADER_MESSAGES['header.actions.register'])}
                </a>
              </>
            )}
          </div>

          <button
            type="button"
            className="indigo-custom-header__mobile-toggle"
            aria-label={intl.formatMessage(HEADER_MESSAGES['header.mobile.menu'])}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((open) => !open)}
          >
            <Icon src={mobileOpen ? Close : MenuIcon} />
          </button>
        </div>

        <div className={`indigo-custom-header__mobile-panel${mobileOpen ? ' open' : ''}`}>
          {NAV_KEYS.map((navKey) => renderNavLink(navKey, { onClick: closeMobile, keyPrefix: 'mobile-' }))}
          <LanguageMenu />
          {authenticatedUser ? (
            userMenuLinks.map((item) => (
              <a key={`mobile-${item.key}`} href={item.href} onClick={closeMobile}>
                {intl.formatMessage(HEADER_MESSAGES[item.messageKey])}
              </a>
            ))
          ) : (
            <>
              <a
                href={loginUrl}
                className="indigo-custom-header__btn indigo-custom-header__btn--ghost"
                onClick={closeMobile}
              >
                {intl.formatMessage(HEADER_MESSAGES['header.actions.signIn'])}
              </a>
              <a
                href={registerUrl}
                className="indigo-custom-header__btn indigo-custom-header__btn--primary"
                onClick={closeMobile}
              >
                {intl.formatMessage(HEADER_MESSAGES['header.actions.register'])}
              </a>
            </>
          )}
        </div>
      </header>
    </>
  );
};
    if (process.env.APP_ID == 'admin-console') {
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'authn') {
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'authoring') {
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'account') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'indigo_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_desktop.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_header_desktop',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: CustomHeader,
                },
            },
]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_header_mobile',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: CustomHeader,
                },
            },
]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'communications') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'indigo_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_learning.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_header_learning',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: CustomHeader,
                },
            },
]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'discussions') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'indigo_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_learning.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_header_learning',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: CustomHeader,
                },
            },
]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'gradebook') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'indigo_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_desktop.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_header_desktop',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: CustomHeader,
                },
            },
]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_header_mobile',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: CustomHeader,
                },
            },
]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'learner-dashboard') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'indigo_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_desktop.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_header_desktop',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: CustomHeader,
                },
            },
]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_header_mobile',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: CustomHeader,
                },
            },
]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'learning') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'indigo_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_learning.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_header_learning',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: CustomHeader,
                },
            },
]);
      addPlugins(config, 'org.openedx.frontend.layout.header_desktop.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_header_desktop',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: CustomHeader,
                },
            },
]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_header_mobile',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: CustomHeader,
                },
            },
]);
      addPlugins(config, 'learning_help_slot', [
        {
            op: PLUGIN_OPERATIONS.Hide,
            widgetId: 'default_contents',
        }
        ]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'ora-grading') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'indigo_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_learning.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_header_learning',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: CustomHeader,
                },
            },
]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'profile') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'indigo_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_desktop.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_header_desktop',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: CustomHeader,
                },
            },
]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_header_mobile',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: CustomHeader,
                },
            },
]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
if (config.pluginSlots && config.pluginSlots['org.openedx.frontend.layout.footer.v1']) {
  const footerPluginsToInsert =
    config.pluginSlots['org.openedx.frontend.layout.footer.v1'].plugins.filter(plugin => plugin.op === 'insert');

  if (footerPluginsToInsert.length > 2) {
  config.pluginSlots['org.openedx.frontend.layout.footer.v1'].plugins =
    config.pluginSlots['org.openedx.frontend.layout.footer.v1'].plugins.filter(plugin => {
      if (plugin.op === 'insert') {
        const widgetId = plugin.widget?.id;
        return widgetId !== 'indigo_footer';
      }
      return true;
    });
  }
}
  } catch (err) { console.error("env.config.jsx failed to apply: ", err);}

  return config;
}

export default setConfig;